import { InferenceAlgorithm } from '@/services/bayesianApi/models'

// KBE-840 PR 2: the contract between the main thread and the inference worker.
//
// Its own module, importing one enum, because both sides need it and neither may pull
// the other in: `trpc.ts` must not reach the engine, and the worker must not reach the
// tRPC client.

export const INFERENCE_WORKER_PATHS = [
  'bayesian.infer',
  'bayesian.multipleInfer',
  'bayesian.diagnose',
] as const

export type InferenceWorkerPath = (typeof INFERENCE_WORKER_PATHS)[number]

export type InferenceWorkerRequest = {
  id: number
  path: InferenceWorkerPath
  input: unknown
}

/**
 * `data` is `unknown` because that is genuinely what a tRPC link carries -- the
 * procedure's declared return type is what types the caller, not this hop.
 *
 * A failure crosses as a MESSAGE rather than an exception: the worker's own `error`
 * event fires without a request id, so it cannot say which call failed, and only the
 * reply can (`ok: false`).
 */
export type InferenceWorkerResponse =
  | { id: number; ok: true; data: unknown }
  | { id: number; ok: false; message: string }

const wireAlgorithmKeys = ['inference_algorithm', 'inferenceAlgorithm'] as const

/**
 * The engine a wire request selects, read from whichever of the two field names it
 * uses -- `infer`/`multipleInfer` send snake_case (the bayesian-api's own key),
 * `diagnose` sends camelCase. A request naming neither is not an inference request.
 *
 * Reading the INPUT rather than the network is what lets the routing live in a tRPC
 * link: the request already carries everything both engines need (gotcha 38), so the
 * transport can pick one without a hook or a component knowing there is a choice.
 */
export function wireInferenceAlgorithm(input: unknown): string | undefined {
  if (!input || typeof input !== 'object') return undefined
  const record = input as Record<string, unknown>
  for (const key of wireAlgorithmKeys) {
    const value = record[key]
    if (typeof value === 'string') return value
  }
  return undefined
}

/**
 * Whether this operation runs in the worker.
 *
 * `LBP_ON_SERVER` deliberately does not: it means "always server-side", so it
 * takes the HTTP path and the router runs the same engine there. And when there is no
 * `Worker` at all -- SSR, jsdom under vitest -- `LBP_IN_BROWSER` falls through to
 * the server too. That is the member's stated meaning ("in a worker where there IS a
 * browser", gotcha 38), not a fallback for a case we do not expect: the same engine
 * answers either way, so the only difference is which thread blocks.
 */
export function runsInInferenceWorker(path: string, input: unknown): boolean {
  if (!(INFERENCE_WORKER_PATHS as readonly string[]).includes(path))
    return false
  if (typeof Worker === 'undefined') return false
  return wireInferenceAlgorithm(input) === InferenceAlgorithm.LBP_IN_BROWSER
}
